/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions, IDistributorInfo } from '@msdyn365-commerce-modules/retail-actions';
import {
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';
import React from 'react';

/**
 * Distributor selector line item resources.
 */
// @ts-ignore
export interface IDistributorSelectorDistributorLineItemResources {
    currentDistributorText: string;
    locationHeadingText: string;
    primaryContactText: string;
    paymentTypeText: string;
    viewAllDistributorText: string;
    deliveryMethodHeadingText: string;
}

/**
 * Distributor Locator line item props.
 */
export interface IDistributorSelectorLineItemProps {
    mainClassName: string;
    selectDistributorText?: string;
    distributor: IDistributorInfo;
    resources: IDistributorSelectorDistributorLineItemResources;
    index: string;
    currentDistributorId: number | undefined;
    distributorDetailsToBeShown?: string[];
    shouldShowIndex?: boolean;
    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;
    handlers: {
        onSetAsCurrentDistributor(distributorId: number | undefined): void;
        onViewAllDistributorDetails(distributorId: number | undefined): void;
    };
}

/**
 * Set Preferred distributor functionality.
 * @param props -Distributor locator location items.
 * @returns Distributor locator selected location line item action.
 */
const onSetCurrentDistributorClickHandler = (props: IDistributorSelectorLineItemProps) => () => {
    props.handlers.onSetAsCurrentDistributor(props.distributor.OrgUnit?.RecordId);
};

const onViewAllDistributorDetailsClickHandler = (props: IDistributorSelectorLineItemProps) => () => {
    props.handlers.onViewAllDistributorDetails(props.distributor.OrgUnit?.RecordId);
};

/**
 * Renders distributor selector line items.
 * @param props - Distributor selector line items props.
 * @returns - HTML.
 */
const distributorSelectorLineItem: React.FC<IDistributorSelectorLineItemProps> = (props: IDistributorSelectorLineItemProps) => {
    const {
        distributor,
        resources,
        index,
        currentDistributorId,
        telemetryContent,
        shouldShowIndex,
        selectDistributorText,
        distributorDetailsToBeShown,
        mainClassName
    } = props;

    const distributorName: string | undefined = distributor.OrgUnit?.OrgUnitName;
    const isCurrentDistributor = currentDistributorId === distributor.OrgUnit?.RecordId;
    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.CurrentDistributor);
    const currentDistributorAttributes = getTelemetryAttributes(telemetryContent!, payLoad);
    const distributorAddress = distributor.OrgUnit?.OrgUnitAddress?.FullAddress;
    const distributorPhoneNo = distributor.OrgUnit?.OrgUnitAddress?.Phone;
    const deliveryMethods = distributor?.OrgUnitDeliveryOptions?.DeliveryOptions;

    return (
        <div className={`${mainClassName}__line-item`}>
            {isCurrentDistributor ? (
                <div className='current-distributor'>
                    <div className='content'>{resources.currentDistributorText}</div>
                </div>
            ) : null}
            <div className={`${mainClassName}__line-item-header`}>
                {shouldShowIndex && <span className='index'>{index}</span>}
                <span className='distributor-name'>{distributorName}</span>
            </div>
            <div className={`${mainClassName}__line-item-content`}>
                {distributorDetailsToBeShown && distributorDetailsToBeShown.indexOf('location') > -1 ? (
                    <div className={`${mainClassName}__details`}>
                        <span className='heading'>{resources.locationHeadingText}</span>
                        {distributorAddress && (
                            <div className='location-section'>
                                <span className='location-icon'></span>
                                <span className='location'>{distributorAddress}</span>
                            </div>
                        )}
                    </div>
                ) : null}
                {distributorDetailsToBeShown && distributorDetailsToBeShown.indexOf('primaryContact') > -1 ? (
                    <div className={`${mainClassName}__details`}>
                        <span className='heading'>{resources.primaryContactText}</span>
                        {distributorPhoneNo && (
                            <div className='contact-section'>
                                <span className='contact-icon'></span>
                                <span className='contact'>{distributorPhoneNo}</span>
                            </div>
                        )}
                    </div>
                ) : null}
                {distributorDetailsToBeShown && distributorDetailsToBeShown.indexOf('deliveryMethod') > -1 ? (
                    <div className={`${mainClassName}__details`}>
                        <span className='heading'>{resources.deliveryMethodHeadingText}</span>
                        {ArrayExtensions.hasElements(deliveryMethods) && (
                            <div className='delivery-methods-section'>
                                <>
                                    {deliveryMethods?.map(deliveryMethod => {
                                        return (
                                            <span key={deliveryMethod.RecordId} className='delivery-method'>
                                                {deliveryMethod.Description}
                                            </span>
                                        );
                                    })}
                                </>
                            </div>
                        )}
                    </div>
                ) : null}
                <a className={`${mainClassName}__view-all-distributor-details`} onClick={onViewAllDistributorDetailsClickHandler(props)}>
                    {resources.viewAllDistributorText}
                </a>
                {!isCurrentDistributor ? (
                    <button
                        role='checkbox'
                        className={`${mainClassName}__select-distributor`}
                        aria-live='polite'
                        aria-checked='false'
                        aria-label={selectDistributorText}
                        {...currentDistributorAttributes}
                        onClick={onSetCurrentDistributorClickHandler(props)}
                    >
                        {selectDistributorText}
                    </button>
                ) : null}
            </div>
        </div>
    );
};

/**
 * Distributor line item component.
 */
export const DistributorSelectorLineItemComponent = distributorSelectorLineItem as (
    props: IDistributorSelectorLineItemProps
) => JSX.Element;
